import axios from 'axios';
import { Contract, parseUnits, parseEther, randomBytes, hexlify } from 'ethers';
import API_URL from './Backendurl';
import { USDC_TOKEN_ABI, USDC_TOKEN_ADDRESS } from './Constants';
import { isMainnetMode } from './workloobNetwork';
import { requestGasDrip, isInsufficientNativeGasError } from './gasDrip';

const MIN_GAS_ETH = 0.00002;
const USDC_EIP3009_ABI = [
  ...USDC_TOKEN_ABI,
  'function transferWithAuthorization(address from, address to, uint256 value, uint256 validAfter, uint256 validBefore, bytes32 nonce, uint8 v, bytes32 r, bytes32 s) returns (bool)',
];

let relayerConfigCache = null;

async function getRelayerConfig() {
  if (relayerConfigCache) return relayerConfigCache;
  const res = await axios.get(`${API_URL}/api/v1/relayer/config`);
  relayerConfigCache = res.data;
  return relayerConfigCache;
}

const getUsdcPermitVersion = async (token) => {
  try {
    if (typeof token.version === 'function') {
      return String(await token.version());
    }
  } catch (_) {
    // MockUSDC on testnet may not expose version()
  }
  return isMainnetMode() ? '2' : '1';
};

const splitSignature = (signature) => {
  const sig = signature.startsWith('0x') ? signature.slice(2) : signature;
  return {
    v: parseInt(sig.slice(128, 130), 16),
    r: `0x${sig.slice(0, 64)}`,
    s: `0x${sig.slice(64, 128)}`,
  };
};

export async function ensureNativeGasForTx({ walletAddress, ethBalance }) {
  const currentEth = Number(ethBalance || 0);
  if (currentEth >= MIN_GAS_ETH) {
    return { ok: true, funded: false };
  }
  const drip = await requestGasDrip(walletAddress);
  if (drip.funded) {
    return { ok: true, funded: true, txHash: drip.txHash };
  }
  if (drip.reason === 'already_sufficient') {
    return { ok: true, funded: false };
  }
  return { ok: false, error: drip.error || 'Gas drip unavailable' };
}

async function signUsdcTransferWithAuthorization({ signer, from, to, amount }) {
  const { chainId } = await signer.provider.getNetwork();
  const token = new Contract(USDC_TOKEN_ADDRESS, USDC_EIP3009_ABI, signer);
  const nonce = hexlify(randomBytes(32));
  const validAfter = 0;
  const validBefore = Math.floor(Date.now() / 1000) + 60 * 30;

  const domain = {
    name: await token.name(),
    version: await getUsdcPermitVersion(token),
    chainId: Number(chainId),
    verifyingContract: USDC_TOKEN_ADDRESS,
  };
  const types = {
    TransferWithAuthorization: [
      { name: 'from', type: 'address' },
      { name: 'to', type: 'address' },
      { name: 'value', type: 'uint256' },
      { name: 'validAfter', type: 'uint256' },
      { name: 'validBefore', type: 'uint256' },
      { name: 'nonce', type: 'bytes32' },
    ],
  };
  const message = {
    from,
    to,
    value: amount.toString(),
    validAfter,
    validBefore,
    nonce,
  };

  const signature = await signer.signTypedData(domain, types, message);
  const { v, r, s } = splitSignature(signature);

  return { validAfter, validBefore, nonce, v, r, s };
}

async function signErc20PermitForRelayer({
  signer,
  tokenAddress,
  from,
  amount,
  relayerAddress,
}) {
  const { chainId } = await signer.provider.getNetwork();
  const token = new Contract(tokenAddress, USDC_TOKEN_ABI, signer);
  const permitNonce = await token.nonces(from);
  const deadline = Math.floor(Date.now() / 1000) + 60 * 30;

  const domain = {
    name: await token.name(),
    version: await getUsdcPermitVersion(token),
    chainId: Number(chainId),
    verifyingContract: tokenAddress,
  };
  const types = {
    Permit: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
      { name: 'value', type: 'uint256' },
      { name: 'nonce', type: 'uint256' },
      { name: 'deadline', type: 'uint256' },
    ],
  };
  const message = {
    owner: from,
    spender: relayerAddress,
    value: amount.toString(),
    nonce: permitNonce.toString(),
    deadline,
  };

  const signature = await signer.signTypedData(domain, types, message);
  const { v, r, s } = splitSignature(signature);
  return { deadline, v, r, s };
}

/**
 * Transfer ERC20 via relayer when possible (gasless for user).
 * Falls back to on-wallet transfer with optional gas drip.
 */
export async function transferToken({
  signer,
  tokenAddress,
  tokenSymbol,
  from,
  to,
  amountHuman,
  decimals,
  ethBalance,
}) {
  const amount = parseUnits(Number(amountHuman).toFixed(decimals), decimals);
  const isUsdc =
    tokenAddress?.toLowerCase() === USDC_TOKEN_ADDRESS.toLowerCase() || tokenSymbol === 'USDC';

  if (isUsdc) {
    try {
      const config = await getRelayerConfig();
      if (config.supportsUsdcEip3009) {
        const authorization = await signUsdcTransferWithAuthorization({
          signer,
          from,
          to,
          amount,
        });
        const res = await axios.post(`${API_URL}/api/v1/relayer/token/transfer`, {
          tokenAddress: USDC_TOKEN_ADDRESS,
          from,
          to,
          amount: amount.toString(),
          authorization,
        });
        return {
          relayed: true,
          txHash: res.data.txHash,
          blockNumber: res.data.blockNumber,
        };
      }

      const authorization = await signErc20PermitForRelayer({
        signer,
        tokenAddress: USDC_TOKEN_ADDRESS,
        from,
        amount,
        relayerAddress: config.relayerAddress,
      });
      const res = await axios.post(`${API_URL}/api/v1/relayer/token/transfer`, {
        tokenAddress: USDC_TOKEN_ADDRESS,
        from,
        to,
        amount: amount.toString(),
        permit: authorization,
      });
      return {
        relayed: true,
        txHash: res.data.txHash,
        blockNumber: res.data.blockNumber,
      };
    } catch (relayerError) {
      console.warn('Relayer USDC transfer failed, falling back to wallet tx:', relayerError?.message);
    }
  }

  await ensureNativeGasForTx({ walletAddress: from, ethBalance });

  const contract = new Contract(tokenAddress, USDC_TOKEN_ABI, signer);
  const nonce = await signer.getNonce('pending');
  const tx = await contract.transfer(to, amount, { nonce });
  const receipt = await tx.wait();
  return {
    relayed: false,
    txHash: receipt.hash,
    blockNumber: receipt.blockNumber,
    gasUsed: receipt.gasUsed?.toString(),
    gasPrice: receipt.gasPrice?.toString(),
  };
}

/**
 * Native ETH transfer with gas drip when the wallet cannot pay fees.
 */
export async function transferNativeEth({ signer, from, to, amountHuman, ethBalance }) {
  const gas = await ensureNativeGasForTx({ walletAddress: from, ethBalance });
  if (!gas.ok) {
    throw new Error(gas.error || 'Unable to fund gas for transfer');
  }

  const nonce = await signer.getNonce('pending');
  const tx = await signer.sendTransaction({
    to,
    value: parseEther(amountHuman),
    nonce,
  });
  const receipt = await tx.wait();
  return {
    relayed: false,
    txHash: receipt.hash,
    blockNumber: receipt.blockNumber,
    gasUsed: receipt.gasUsed?.toString(),
    gasPrice: receipt.gasPrice?.toString(),
  };
}

export { isInsufficientNativeGasError };
